import React, { useState } from "react";
import { Select } from "@jmc/solid-design-system/src/components/atoms/Select/Select";
import { Flag } from "@jmc/solid-design-system/src/components/atoms/Flag/Flag";
import { Typography } from "@jmc/solid-design-system/src/components/atoms/Typography/Typography";

import style from "./style.module.scss";
import { useJnjBranding } from "@jmc/utils/hooks/useJnjBranding";

interface Props {
    countries: {
        country_iso_code: string;
        country_name: string;
        website_url: string;
        button: JSX.Element;
    }[];
    countrySpecificText?: string;
    placeHolderText?: string;
}

export const CountrySelector = (props: Props): JSX.Element => {
    const { countries, countrySpecificText = "", placeHolderText = "" } = props;
    const [selectedCountry, setSelectedCountry] = useState(countries?.[0]);
    const { jnjFullBranded } = useJnjBranding();

    return (
        <div className={style.wrapper}>
            <div className={style.container}>
                <div className={style.textSection}>
                    <Typography
                        color={jnjFullBranded ? "text-01" : "primary-general"}
                        font="title"
                        variant={jnjFullBranded ? undefined : "h5"}
                        weight={jnjFullBranded ? "400" : "500"}
                        size={jnjFullBranded ? "4xl" : "l"}
                        component="span"
                    >
                        {countrySpecificText}
                    </Typography>
                </div>
                <div className={style.countryContainer}>
                    <Select
                        isSearchable={false}
                        data-test-id="CountryInput.Select"
                        placeholder={placeHolderText}
                        ariaLabel={placeHolderText}
                        onChange={(option) => {
                            setSelectedCountry(option?.value);
                        }}
                        name="countrySelector"
                        option={jnjFullBranded ? undefined : { backgroundColor: "var(--grey-light)" }}
                    >
                        {countries?.map((country) => (
                            <Select.Option
                                key={country?.country_iso_code}
                                value={country}
                                selected={selectedCountry?.country_iso_code === country?.country_iso_code}
                                data-test-id="CountryInput.Option"
                                ariaLabel={country?.country_name}
                            >
                                <div className={style.option}>
                                    <div
                                        className={style.flag}
                                        data-test-id={`CountryInput.Option.${country?.country_iso_code}`}
                                        tabIndex={-1}
                                    >
                                        <Flag code={country?.country_iso_code} />
                                    </div>
                                    <div
                                        className={style.countryName}
                                        data-test-id={`CountryInput.Option.${country?.country_name}`}
                                        role="option"
                                        aria-selected={selectedCountry?.country_iso_code === country?.country_iso_code}
                                    >
                                        <Typography
                                            variant="span"
                                            font="text"
                                            color={jnjFullBranded ? "inherit" : undefined}
                                            weight={jnjFullBranded ? "inherit" : undefined}
                                        >
                                            {country?.country_name}
                                        </Typography>
                                    </div>
                                </div>
                            </Select.Option>
                        ))}
                    </Select>
                </div>
                <div className={style.buttonSection}>{selectedCountry?.button}</div>
            </div>
        </div>
    );
};
